import React, { useEffect, useState } from 'react'
import { Form, Button, Container, Card, Row, Col } from 'react-bootstrap'
import 'bootstrap/dist/css/bootstrap.min.css'
import toast from 'react-hot-toast'
import { useNavigate } from 'react-router-dom'
import ApiEndpoints from './commonComponents/ApiEndpoints'
import { FaUniversity } from 'react-icons/fa'
import { LuGraduationCap } from 'react-icons/lu'
import { useAuth } from './AuthContext'
import { useSidebar } from './SidebarContext'
import ProfileDropdown from './commonComponents/ProfileDropdown'
import { useInstitute } from './commonComponents/InstituteContext'

const AddNewInstitute = () => {
  const { logout } = useAuth()
  const { isCollapsed } = useSidebar()
  const navigate = useNavigate()
  const { logo } = useInstitute()
  const [logoError, setLogoError] = useState(false)
  const [marginLeft, setMarginLeft] = useState('0px')
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth <= 768 : false,
  )
  const [loading, setLoading] = useState(false)
  const [logoFile, setLogoFile] = useState(null)
  const [stampFile, setStampFile] = useState(null)
  const [newInstitute, setNewInstitute] = useState({
    name: '',
    address: '',
    pincode: '',
  })

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 992) {
        const expandedWidth = 245
        const collapsedWidth = 85
        const gap = 1

        setMarginLeft(
          isCollapsed
            ? `${collapsedWidth + gap}px`
            : `${expandedWidth + gap}px`,
        )
      } else {
        setMarginLeft('0px')
      }

      setIsMobile(window.innerWidth <= 768)
    }

    handleResize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [isCollapsed])

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setNewInstitute((prev) => ({ ...prev, [name]: value }))
  }

  const resolveNewInstituteId = (response) => {
    const payload = response?.data?.data ?? response?.data
    if (!payload || typeof payload !== 'object') return null
    return payload.institute_id ?? payload.id ?? null
  }

  const handleSave = async (e) => {
    e.preventDefault()
    try {
      setLoading(true)
      const institutePayload = {
        name: newInstitute.name,
        address: newInstitute.address,
        pincode: newInstitute.pincode,
      }

      if (logoFile && logoFile.size > 2 * 1024 * 1024) {
        toast.error('Logo image size should not exceed 2MB.')
        return
      }

      if (stampFile && stampFile.size > 2 * 1024 * 1024) {
        toast.error('Stamp image size should not exceed 2MB.')
        return
      }

      const response = await ApiEndpoints.addInstitute(institutePayload)
      const instituteId = resolveNewInstituteId(response)

      let savedLogoUrl = null

      if (logoFile) {
        if (!instituteId) {
          toast.error(
            'Institute was created but logo could not be linked (missing ID).',
          )
        } else {
          try {
            const formData = new FormData()
            formData.append('image', logoFile)
            const logoResponse = await ApiEndpoints.uploadInstituteLogo(
              instituteId,
              formData,
            )
            const logoUrl =
              logoResponse.data?.url ?? logoResponse.data?.data?.url ?? null
            if (!logoUrl) {
              toast.error(
                'Institute saved, but logo URL was not returned from the server.',
              )
            } else {
              savedLogoUrl = logoUrl
              await ApiEndpoints.editInstitute(instituteId, {
                ...institutePayload,
                logo: logoUrl,
              })
            }
          } catch (logoErr) {
            toast.error(
              logoErr.response?.data?.message ||
                logoErr.message ||
                'Institute saved, but logo upload failed.',
            )
          }
        }
      }

      if (stampFile) {
        if (!instituteId) {
          toast.error(
            'Institute was created but stamp could not be linked (missing ID).',
          )
        } else {
          try {
            const formData = new FormData()
            formData.append('image', stampFile)
            const stampResponse = await ApiEndpoints.uploadInstituteLogo(
              instituteId,
              formData,
            )
            const stampUrl =
              stampResponse.data?.url ?? stampResponse.data?.data?.url ?? null
            if (!stampUrl) {
              toast.error(
                'Institute saved, but stamp URL was not returned from the server.',
              )
            } else {
              await ApiEndpoints.editInstitute(instituteId, {
                ...institutePayload,
                ...(savedLogoUrl ? { logo: savedLogoUrl } : {}),
                stamp_url_img: stampUrl,
              })
            }
          } catch (stampErr) {
            toast.error(
              stampErr.response?.data?.message ||
                stampErr.message ||
                'Institute saved, but stamp upload failed.',
            )
          }
        }
      }

      setNewInstitute({
        name: '',
        address: '',
        pincode: '',
      })
      setLogoFile(null)
      setStampFile(null)

      toast.success(response.data.message)
      navigate('/AddInstitute')
    } catch (error) {
      toast.error(
        error.response?.data?.message ||
          error.response?.message ||
          'Something went wrong.',
      )
      navigate('/AddInstitute')
    } finally {
      setLoading(false)
    }
  }

  const handleCancel = () => {
    navigate('/AddInstitute')
  }

  return (
    <>
      <div
        style={{
          marginLeft,
          transition: 'margin-left 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
          willChange: 'margin-left',
          WebkitTransition: 'margin-left 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
          position: 'relative',
          zIndex: 1,
          marginTop: '0px',
          marginRight: '0px',
          marginBottom: '0px',
          paddingLeft: isCollapsed ? '12px' : '12px',
          paddingRight: isCollapsed ? '12px' : '12px',
        }}
      >
        {/* Header */}
        <div
          className="container-fluid p-0"
          style={{
            marginTop: window.innerWidth < 992 ? '55px' : '-12px',
            paddingLeft: '0px',
            paddingRight: '0px',
          }}
        >
          <div style={{ marginLeft: isMobile ? '-13px' : '-40px' }}>
            <div
              className="d-flex justify-content-between align-items-center"
              style={{
                background: '#F3F4F6',
                paddingLeft: '28px',
                paddingRight: '28px',
                paddingTop: '12px',
                paddingBottom: '12px',
                overflow: 'visible',
                borderBottom: '1px solid #D1D3D4',
                width: '100%',
              }}
            >
              <div className="d-flex align-items-center gap-3">
                <div
                  style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    overflow: 'hidden',
                    background: '#223843',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  {!logoError ? (
                    <img
                      src={logo}
                      alt="School Logo"
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      onError={() => setLogoError(true)}
                    />
                  ) : (
                    <LuGraduationCap size={20} style={{ color: '#ffffff' }} />
                  )}
                </div>

                <h3
                  className="m-0 fw-bold"
                  style={{
                    letterSpacing: '-0.02em',
                    overflow: 'visible',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    fontSize: 'clamp(18px, 2.5vw, 22px)',
                    color: '#111827',
                    lineHeight: '1.2',
                    fontFamily: 'Inter, system-ui, sans-serif',
                  }}
                >
                  Add New Institute
                </h3>
              </div>

              {!isMobile && <ProfileDropdown logout={logout} />}
            </div>
          </div>
        </div>

        <Container
          className="mt-4"
          style={{
            paddingLeft: isCollapsed ? '32px' : '40px',
            paddingRight: '32px',
            paddingTop: '32px',
            paddingBottom: '32px',
            maxWidth: '1200px',
          }}
        >
          <Form onSubmit={handleSave}>
            <Row className="g-4">
              <Col lg={8} className="d-flex">
                <Card
                  className="h-100 w-100"
                  style={{
                    border: '1px solid #D1D3D4',
                    borderRadius: '12px',
                    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                    overflow: 'hidden',
                  }}
                >
                  <div
                    style={{
                      background:
                        'linear-gradient(135deg, #223843 0%, #223843 100%)',
                      padding: '16px 24px',
                      borderBottom: '1px solid #D1D3D4',
                    }}
                  >
                    <div className="d-flex align-items-center gap-2">
                      <div
                        style={{
                          width: '36px',
                          height: '36px',
                          borderRadius: '10px',
                          backgroundColor: 'rgba(255, 255, 255, 0.2)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backdropFilter: 'blur(10px)',
                        }}
                      >
                        <FaUniversity size={18} style={{ color: '#ffffff' }} />
                      </div>
                      <h5
                        style={{
                          margin: 0,
                          fontWeight: '700',
                          fontSize: '16px',
                          color: '#ffffff',
                          fontFamily: 'Inter, system-ui, sans-serif',
                        }}
                      >
                        Institute Details
                      </h5>
                    </div>
                  </div>

                  <Card.Body style={{ padding: '24px' }} className="text-start">
                    <Form.Group className="mb-3">
                      <Form.Label>Institute Name</Form.Label>
                      <Form.Control
                        type="text"
                        name="name"
                        value={newInstitute.name}
                        onChange={handleInputChange}
                        placeholder="Enter Institute Name"
                        className="p-2 border rounded"
                        required
                      />
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Address</Form.Label>
                      <Form.Control
                        type="textarea"
                        name="address"
                        value={newInstitute.address}
                        onChange={handleInputChange}
                        placeholder="Enter Address"
                        className="p-2 border rounded"
                        required
                      />
                    </Form.Group>

                    <Form.Group className="mb-0">
                      <Form.Label>Pincode</Form.Label>
                      <Form.Control
                        type="number"
                        name="pincode"
                        value={newInstitute.pincode}
                        onChange={handleInputChange}
                        placeholder="Enter Pincode"
                        className="p-2 border rounded"
                        required
                      />
                    </Form.Group>
                  </Card.Body>
                </Card>
              </Col>

              <Col lg={4} className="d-flex">
                <Card
                  className="h-100 w-100"
                  style={{
                    border: '1px solid #D1D3D4',
                    borderRadius: '12px',
                    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                    overflow: 'hidden',
                  }}
                >
                  <div
                    style={{
                      background:
                        'linear-gradient(135deg, #223843 0%, #223843 100%)',
                      padding: '16px 24px',
                      borderBottom: '1px solid #D1D3D4',
                    }}
                  >
                    <h5
                      style={{
                        margin: 0,
                        fontWeight: '700',
                        fontSize: '16px',
                        color: '#ffffff',
                        fontFamily: 'Inter, system-ui, sans-serif',
                      }}
                    >
                      Institute Logo
                    </h5>
                  </div>

                  <Card.Body style={{ padding: '24px' }} className="text-start">
                    <Form.Group className="mb-3">
                      <Form.Label>Institute logo (optional)</Form.Label>
                      <Form.Control
                        type="file"
                        accept="image/png, image/jpeg, image/jpg, image/webp"
                        onChange={(e) =>
                          setLogoFile(e.target.files?.[0] ?? null)
                        }
                        className="p-2 border rounded"
                      />
                      <Form.Text className="text-muted">
                        Max 2MB. Uploaded after the institute is created.
                      </Form.Text>
                    </Form.Group>

                    {logoFile && (
                      <div className="d-flex justify-content-start">
                        <img
                          src={URL.createObjectURL(logoFile)}
                          alt="Logo preview"
                          style={{
                            width: 96,
                            height: 96,
                            objectFit: 'cover',
                            borderRadius: 12,
                            border: '1px solid #D1D3D4',
                          }}
                        />
                      </div>
                    )}

                    <Form.Group className="mb-3 mt-3">
                      <Form.Label>Institute stamp (optional)</Form.Label>
                      <Form.Control
                        type="file"
                        accept="image/png, image/jpeg, image/jpg, image/webp"
                        onChange={(e) =>
                          setStampFile(e.target.files?.[0] ?? null)
                        }
                        className="p-2 border rounded"
                      />
                      <Form.Text className="text-muted">
                        Max 2MB. Uploaded after the institute is created.
                      </Form.Text>
                    </Form.Group>

                    {stampFile && (
                      <div className="d-flex justify-content-start">
                        <img
                          src={URL.createObjectURL(stampFile)}
                          alt="Stamp preview"
                          style={{
                            width: 96,
                            height: 96,
                            objectFit: 'cover',
                            borderRadius: 12,
                            border: '1px solid #D1D3D4',
                          }}
                        />
                      </div>
                    )}

                    <div className="d-flex justify-content-end gap-2 mt-4">
                      <Button
                        variant="danger"
                        className="rounded-pill px-4"
                        type="button"
                        onClick={handleCancel}
                        disabled={loading}
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="success"
                        className="rounded-pill px-4"
                        type="submit"
                        disabled={loading}
                      >
                        {loading ? 'Saving…' : 'Save Institute'}
                      </Button>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Form>
        </Container>
      </div>
    </>
  )
}

export default AddNewInstitute

